import React, { useState } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';

// Configuración del WhatsApp automático a los leads nuevos del webhook (/google-ads).
// El estado vive en la página (se guarda con el botón "Guardar" del modal de Configuración);
// aquí solo se edita y se envían pruebas.

const SOURCE_LABELS = {
  nombre: 'Nombre del lead (solo el nombre)',
  nombre_completo: 'Nombre completo',
  servicio: 'Servicio de la landing',
  campana: 'Campaña',
  email: 'Email del lead',
  telefono: 'Teléfono del lead',
  texto: 'Texto fijo'
};

const SAMPLE = {
  nombre: 'Miguel',
  nombre_completo: 'Miguel López Barbero',
  servicio: 'Integrar IA en empresa (Meta Ads)',
  email: 'miguel@ejemplo.com',
  telefono: '+34 600 000 000'
};

const AI_REPLY_OPTIONS = [
  { value: 'auto', label: 'La IA le responde sola siguiendo el guion' },
  { value: 'draft', label: 'La IA prepara un borrador y lo revisas tú' },
  { value: 'off', label: 'Sin IA: lo atiendes tú' }
];

const STATUS_LABELS = { programado: 'programados', enviado: 'enviados', error: 'con error', omitido: 'omitidos', sin_regla: 'sin regla', enviando: 'enviando' };

// Variable de plantilla → origen del dato por defecto
const guessSource = (variable, index) => {
  const v = String(variable).toLowerCase();
  if (['nombre', 'name', 'first_name', 'cliente'].includes(v)) return { source: 'nombre' };
  if (['servicio', 'service', 'producto'].includes(v)) return { source: 'servicio' };
  if (['campana', 'campaña', 'campaign'].includes(v)) return { source: 'campana' };
  return index === 0 ? { source: 'nombre' } : { source: 'texto', value: '' };
};

const renderPreview = (tpl, rule) => {
  if (!tpl) return '';
  let text = String(tpl.bodyText || '');
  (tpl.bodyVars || []).forEach((v, i) => {
    const map = (rule.variables || {})[v] || guessSource(v, i);
    const value = map.source === 'texto'
      ? (map.value || `[texto {{${v}}} vacío]`)
      : (map.source === 'campana' ? (rule.campaign || 'ia-automatizacion') : SAMPLE[map.source]);
    text = text.split(`{{${v}}}`).join(value);
  });
  return text;
};

// Antes de guardar: cada variable de la plantilla queda con su origen explícito (el mismo que
// muestra la vista previa), para que lo enviado coincida con lo que se ve en pantalla.
export const completeRuleVariables = (config, templates = []) => ({
  ...config,
  dedupeDays: config.dedupeDays === '' || config.dedupeDays == null ? 7 : config.dedupeDays,
  rules: (config.rules || []).map((rule) => {
    const tpl = templates.find((t) => t.name === rule.template && (!rule.language || t.language === rule.language))
      || templates.find((t) => t.name === rule.template);
    if (!tpl) return rule;
    const variables = {};
    (tpl.bodyVars || []).forEach((v, i) => { variables[v] = (rule.variables || {})[v] || guessSource(v, i); });
    return { ...rule, language: rule.language || tpl.language, variables };
  })
});

const newRuleId = () => `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const LeadWhatsappConfig = ({ data, config, onChange, onRefreshTemplates, refreshing, token, onUnauthorized }) => {
  const [testPhone, setTestPhone] = useState({});
  const [testState, setTestState] = useState({});

  const templates = data?.templates || [];
  const campaigns = data?.campaigns || [];
  const findTemplate = (rule) => templates.find((t) => t.name === rule.template && (!rule.language || t.language === rule.language))
    || templates.find((t) => t.name === rule.template) || null;

  const update = (patch) => onChange({ ...config, ...patch });
  const metaAds = { enabled: false, aiReply: 'auto', aiScript: '', callMinutes: 15, textPatterns: [], createLead: true, ...(config.metaAds || {}) };
  const updateMeta = (patch) => update({ metaAds: { ...metaAds, ...patch } });
  const patternsText = Array.isArray(metaAds.textPatterns) ? metaAds.textPatterns.join('\n') : String(metaAds.textPatterns || '');
  const updateRule = (id, patch) => update({ rules: config.rules.map((r) => (r.id === id ? { ...r, ...patch } : r)) });
  const removeRule = (id) => update({ rules: config.rules.filter((r) => r.id !== id) });
  const addRule = () => update({
    rules: [...config.rules, {
      id: newRuleId(),
      campaign: '',
      template: '',
      language: '',
      variables: {},
      enabled: true,
      aiReply: 'auto',
      aiScript: data?.defaultScript || '',
      callMinutes: 15
    }]
  });

  const chooseTemplate = (rule, key) => {
    const [name, language] = key.split('|');
    const tpl = templates.find((t) => t.name === name && t.language === language);
    const variables = {};
    (tpl?.bodyVars || []).forEach((v, i) => { variables[v] = (rule.variables || {})[v] || guessSource(v, i); });
    updateRule(rule.id, { template: name || '', language: language || '', variables });
  };

  const setVariable = (rule, variable, patch) => {
    const current = (rule.variables || {})[variable] || { source: 'texto', value: '' };
    updateRule(rule.id, { variables: { ...(rule.variables || {}), [variable]: { ...current, ...patch } } });
  };

  const sendTest = async (rule) => {
    const phone = (testPhone[rule.id] || '').trim();
    if (!phone) { setTestState((s) => ({ ...s, [rule.id]: { error: 'Escribe un teléfono para la prueba' } })); return; }
    setTestState((s) => ({ ...s, [rule.id]: { sending: true } }));
    try {
      const res = await apiFetch('/api/google-ads/lead-whatsapp/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rule, phone })
      });
      const out = await parseJsonSafe(res);
      if (res.status === 401) return onUnauthorized();
      if (!res.ok) throw new Error(out.message || 'No se pudo enviar la prueba');
      setTestState((s) => ({ ...s, [rule.id]: { ok: `Prueba enviada a +${out.phone}${(rule.aiReply || 'auto') !== 'off' ? '. Contesta desde ese móvil para probar a la IA.' : ''}` } }));
    } catch (err) {
      setTestState((s) => ({ ...s, [rule.id]: { error: err.message } }));
    }
    return null;
  };

  const duplicatedCampaigns = (() => {
    const seen = new Set();
    const dup = new Set();
    config.rules.filter((r) => r.enabled).forEach((r) => {
      const k = (r.campaign || '').toLowerCase();
      if (seen.has(k)) dup.add(k);
      seen.add(k);
    });
    return dup;
  })();

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-2">
        <div>
          <div className="fw-semibold">WhatsApp automático a los leads nuevos</div>
          <div className="text-muted small">
            La plantilla de su campaña se envía 5 minutos después de que entre el lead por el webhook. Tiene que ser una plantilla aprobada en Meta:
            el lead aún no nos ha escrito y WhatsApp solo permite iniciar la conversación así. El mensaje queda en /whatsapp.
          </div>
        </div>
        <div className="form-check form-switch">
          <input className="form-check-input" type="checkbox" id="leadWaEnabled" checked={Boolean(config.enabled)} onChange={(e) => update({ enabled: e.target.checked })} />
          <label className="form-check-label fw-semibold" htmlFor="leadWaEnabled">{config.enabled ? 'Activado' : 'Desactivado'}</label>
        </div>
      </div>

      {Array.isArray(data?.stats) && data.stats.length > 0 && (
        <div className="small text-muted mb-2">
          Últimos 30 días: {data.stats.map((s) => `${s.n} ${STATUS_LABELS[s.status] || s.status}`).join(' · ')}
        </div>
      )}

      <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
        <label className="small mb-0" htmlFor="leadWaDedupe">No repetir al mismo teléfono durante</label>
        <input
          id="leadWaDedupe"
          type="number"
          min="0"
          max="365"
          className="form-control form-control-sm"
          style={{ width: 80 }}
          value={config.dedupeDays}
          onChange={(e) => update({ dedupeDays: e.target.value === '' ? '' : Number(e.target.value) })}
        />
        <span className="small">días</span>
        <button type="button" className="btn btn-sm btn-outline-secondary ms-auto" onClick={onRefreshTemplates} disabled={refreshing}>
          {refreshing ? 'Actualizando…' : 'Actualizar plantillas de Meta'}
        </button>
      </div>

      {data?.templatesError && <div className="alert alert-warning py-2 small">{data.templatesError}</div>}
      {!data?.templatesError && templates.length === 0 && (
        <div className="alert alert-warning py-2 small">No hay plantillas aprobadas sin adjunto. Crea una en Meta (WhatsApp Manager) y pulsa «Actualizar plantillas de Meta».</div>
      )}

      {config.rules.length === 0 && (
        <div className="text-muted small mb-2">Sin reglas. Añade una regla para «Cualquier campaña» o para una campaña concreta.</div>
      )}

      {config.rules.map((rule) => {
        const tpl = findTemplate(rule);
        const missing = rule.template && !tpl;
        const st = testState[rule.id] || {};
        const dup = rule.enabled && duplicatedCampaigns.has((rule.campaign || '').toLowerCase());
        return (
          <div key={rule.id} className={`border rounded p-2 mb-2 ${rule.enabled ? '' : 'opacity-75 bg-light'}`}>
            <div className="row g-2 align-items-end">
              <div className="col-md-5">
                <label className="form-label small mb-1">Campaña (utm_campaign)</label>
                <input
                  className="form-control form-control-sm"
                  list={`leadWaCampaigns-${rule.id}`}
                  placeholder="Vacío = cualquier campaña (por defecto)"
                  value={rule.campaign}
                  onChange={(e) => updateRule(rule.id, { campaign: e.target.value })}
                />
                <datalist id={`leadWaCampaigns-${rule.id}`}>
                  {campaigns.map((c) => <option key={c.campaign} value={c.campaign}>{`${c.leads} leads`}</option>)}
                </datalist>
              </div>
              <div className="col-md-5">
                <label className="form-label small mb-1">Plantilla de WhatsApp</label>
                <select
                  className={`form-select form-select-sm ${missing ? 'is-invalid' : ''}`}
                  value={rule.template ? `${rule.template}|${rule.language || tpl?.language || ''}` : ''}
                  onChange={(e) => chooseTemplate(rule, e.target.value)}
                >
                  <option value="">Elige una plantilla…</option>
                  {missing && <option value={`${rule.template}|${rule.language}`}>{rule.template} ({rule.language}) — ya no está aprobada</option>}
                  {templates.map((t) => (
                    <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                      {t.name} ({t.language}){t.category ? ` · ${t.category === 'MARKETING' ? 'marketing' : t.category.toLowerCase()}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 d-flex gap-2 align-items-center justify-content-md-end">
                <div className="form-check form-switch mb-0">
                  <input className="form-check-input" type="checkbox" id={`leadWaRule-${rule.id}`} checked={rule.enabled} onChange={(e) => updateRule(rule.id, { enabled: e.target.checked })} />
                  <label className="form-check-label small" htmlFor={`leadWaRule-${rule.id}`}>Activa</label>
                </div>
                <button type="button" className="btn btn-sm btn-outline-danger" title="Eliminar regla" onClick={() => removeRule(rule.id)}>×</button>
              </div>
            </div>
            {dup && <div className="text-danger small mt-1">Hay otra regla activa para esta misma campaña: solo se usará la primera.</div>}

            {tpl && (tpl.bodyVars || []).length > 0 && (
              <div className="mt-2">
                <div className="small fw-semibold mb-1">Variables de la plantilla</div>
                {(tpl.bodyVars || []).map((v, i) => {
                  const map = (rule.variables || {})[v] || guessSource(v, i);
                  return (
                    <div key={v} className="d-flex gap-2 align-items-center mb-1 flex-wrap">
                      <code className="small" style={{ minWidth: 70 }}>{`{{${v}}}`}</code>
                      <select className="form-select form-select-sm" style={{ maxWidth: 260 }} value={map.source} onChange={(e) => setVariable(rule, v, { source: e.target.value })}>
                        {Object.entries(SOURCE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                      </select>
                      {map.source === 'texto' && (
                        <input className="form-control form-control-sm" style={{ maxWidth: 280 }} placeholder="Texto que se enviará" value={map.value || ''} onChange={(e) => setVariable(rule, v, { value: e.target.value })} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {tpl && (
              <div className="mt-2">
                <div className="small text-muted mb-1">Vista previa (con un lead de ejemplo)</div>
                {tpl.headerText && <div className="small fw-semibold">{tpl.headerText}</div>}
                <div className="small border rounded p-2" style={{ whiteSpace: 'pre-wrap', background: '#e8f5ff', borderColor: '#bcdcf5' }}>{renderPreview(tpl, rule)}</div>
              </div>
            )}

            <div className="mt-3 pt-2 border-top">
              <div className="row g-2 align-items-end">
                <div className="col-md-8">
                  <label className="form-label small mb-1 fw-semibold">Si el lead contesta al WhatsApp</label>
                  <select className="form-select form-select-sm" value={rule.aiReply || 'auto'} onChange={(e) => updateRule(rule.id, { aiReply: e.target.value })}>
                    {AI_REPLY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {(rule.aiReply || 'auto') !== 'off' && (
                  <div className="col-md-4">
                    <label className="form-label small mb-1">Duración de la llamada</label>
                    <select className="form-select form-select-sm" value={[10, 15].includes(Number(rule.callMinutes)) ? rule.callMinutes : 15} onChange={(e) => updateRule(rule.id, { callMinutes: Number(e.target.value) })}>
                      {[10, 15].map((m) => <option key={m} value={m}>{m} minutos</option>)}
                    </select>
                  </div>
                )}
              </div>
              {(rule.aiReply || 'auto') !== 'off' && (
                <div className="mt-2">
                  <div className="d-flex justify-content-between align-items-end gap-2 flex-wrap mb-1">
                    <label className="form-label small mb-0">Guion para la IA</label>
                    {data?.defaultScript && rule.aiScript !== data.defaultScript && (
                      <button type="button" className="btn btn-link btn-sm p-0" onClick={() => updateRule(rule.id, { aiScript: data.defaultScript })}>Usar el guion por defecto</button>
                    )}
                  </div>
                  <textarea
                    className="form-control form-control-sm"
                    rows={6}
                    placeholder={data?.defaultScript || 'Qué debe conseguir la IA con este lead y cómo'}
                    value={rule.aiScript || ''}
                    onChange={(e) => updateRule(rule.id, { aiScript: e.target.value })}
                  />
                  <div className="text-muted small mt-1">
                    La IA conoce el mensaje que se le envió, el servicio y la campaña del lead, y ofrece para la llamada solo huecos libres de tu agenda en horario laboral.
                    {(rule.aiReply || 'auto') === 'auto'
                      ? ' Cuando el lead acepta un hueco, crea la llamada en Google Calendar y te avisa con una notificación.'
                      : ' Prepara la respuesta (y la cita, si la acuerda) como borrador en /whatsapp para que la apruebes.'}
                    {' '}Si escribes tú en esa conversación, la IA deja de responder sola en ella. Si el guion queda vacío se usa el guion por defecto.
                  </div>
                </div>
              )}
            </div>

            {tpl && (
              <div className="d-flex gap-2 align-items-center mt-2 flex-wrap">
                <input
                  className="form-control form-control-sm"
                  style={{ maxWidth: 200 }}
                  placeholder="Tu móvil para la prueba"
                  value={testPhone[rule.id] || ''}
                  onChange={(e) => setTestPhone((s) => ({ ...s, [rule.id]: e.target.value }))}
                />
                <button type="button" className="btn btn-sm btn-outline-primary" disabled={st.sending} onClick={() => sendTest(rule)}>
                  {st.sending ? 'Enviando…' : 'Enviar prueba'}
                </button>
                {st.ok && <span className="small text-primary">{st.ok}</span>}
                {st.error && <span className="small text-danger">{st.error}</span>}
              </div>
            )}
          </div>
        );
      })}

      <button type="button" className="btn btn-sm btn-outline-primary" onClick={addRule}>+ Añadir regla</button>
      <div className="text-muted small mt-2">
        Si la campaña del lead no tiene regla propia se usa la de «cualquier campaña». Los cambios se aplican al pulsar «Guardar».
      </div>

      <hr />
      <div className={`border rounded p-2 ${metaAds.enabled ? '' : 'opacity-75 bg-light'}`}>
        <div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-2">
          <div>
            <div className="fw-semibold">Anuncios de Meta «clic a WhatsApp»</div>
            <div className="text-muted small">
              Aquí no hay formulario ni plantilla: el primer contacto es el propio WhatsApp. Meta marca ese primer mensaje con el anuncio
              del que viene; si está activado, la conversación entra en modo lead al instante (la IA responde sola con el guion y propone
              la llamada) y el lead se crea en el tablero con canal Whatsapp y el titular del anuncio como campaña. El resto de WhatsApp
              sigue igual que ahora. Si escribes tú en esa conversación, la IA se aparta.
            </div>
          </div>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" id="leadWaMetaEnabled" checked={Boolean(metaAds.enabled)} onChange={(e) => updateMeta({ enabled: e.target.checked })} />
            <label className="form-check-label fw-semibold" htmlFor="leadWaMetaEnabled">{metaAds.enabled ? 'Activado' : 'Desactivado'}</label>
          </div>
        </div>
        <div className="row g-2 mb-2">
          <div className="col-md-4">
            <label className="form-label small mb-1">Cuando escriba el lead</label>
            <select className="form-select form-select-sm" value={metaAds.aiReply || 'auto'} onChange={(e) => updateMeta({ aiReply: e.target.value })}>
              <option value="auto">La IA responde sola</option>
              <option value="draft">La IA deja un borrador para aprobar</option>
              <option value="off">Sin IA</option>
            </select>
          </div>
          {(metaAds.aiReply || 'auto') !== 'off' && (
            <div className="col-md-3">
              <label className="form-label small mb-1">Duración de la llamada</label>
              <select className="form-select form-select-sm" value={[10, 15].includes(Number(metaAds.callMinutes)) ? metaAds.callMinutes : 15} onChange={(e) => updateMeta({ callMinutes: Number(e.target.value) })}>
                {[10, 15].map((m) => <option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
          )}
          <div className="col-md-5">
            <label className="form-label small mb-1">Crear el lead en el tablero</label>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" id="leadWaMetaCreate" checked={metaAds.createLead !== false} onChange={(e) => updateMeta({ createLead: e.target.checked })} />
              <label className="form-check-label small" htmlFor="leadWaMetaCreate">Sí, con canal «Whatsapp» (si ya existe por teléfono, se reutiliza)</label>
            </div>
          </div>
        </div>
        {(metaAds.aiReply || 'auto') !== 'off' && (
          <div className="mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <label className="form-label small mb-1">Guion para la IA</label>
              {data?.defaultScript && metaAds.aiScript !== data.defaultScript && (
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => updateMeta({ aiScript: data.defaultScript })}>Usar el guion por defecto</button>
              )}
            </div>
            <textarea
              className="form-control form-control-sm"
              rows={5}
              value={metaAds.aiScript || ''}
              placeholder={data?.defaultScript || ''}
              onChange={(e) => updateMeta({ aiScript: e.target.value })}
            />
            <div className="form-text">
              La IA sabe que el lead viene de un anuncio (y cuál), se presenta, y ofrece para la llamada solo huecos libres de tu agenda en horario laboral. Si queda vacío se usa el guion por defecto.
            </div>
          </div>
        )}
        <div>
          <label className="form-label small mb-1">Frases de respaldo (una por línea)</label>
          <textarea
            className="form-control form-control-sm"
            rows={2}
            value={patternsText}
            placeholder={'quiero información sobre integrar la IA\ndiagnóstico gratuito de IA'}
            onChange={(e) => updateMeta({ textPatterns: e.target.value.split(/\r?\n/) })}
          />
          <div className="form-text">
            Por si algún mensaje llegara sin la marca del anuncio: si el PRIMER mensaje de una conversación nueva contiene una de estas frases (sin importar mayúsculas ni acentos), se trata también como lead de anuncio. Pon aquí el texto que rellena cada anuncio; se ignoran las frases de menos de 10 caracteres.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadWhatsappConfig;
